import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface InventoryItem {
  id: string;
  productId: string;
  branchId?: string | null;
  salesPointId?: string | null;
  warehouseId?: string | null;
  unit?: string;
  quantity: number;
  stockQuantity?: number;
  reservedQuantity: number;
  availableQuantity: number;
  reorderLevel: number;
  minStockAlert?: number;
  lastRestockedAt?: string;
  createdAt: string;
  updatedAt: string;
  product: {
    id: string;
    name: string;
    sku: string;
    productType: "GOODS" | "PREPARED";
    unit?: string;
    baseUnit?: string;
    stockUnit?: string;
    purchaseUnit?: string;
    conversionFactor?: number;
    allowFractionalQty?: boolean;
  };
  branch: {
    id: string;
    name: string;
  };
  warehouse?: {
    id: string;
    name: string;
    code: string;
    type?: string;
    city?: string | null;
  } | null;
}

export interface CreateInventoryData {
  productId: string;
  branchId?: string | null;
  salesPointId: string;
  quantity: number;
  reorderLevel?: number;
  minStockAlert?: number;
}

export interface UpdateInventoryData {
  reorderLevel?: number;
  minStockAlert?: number;
  autoDeductStock?: boolean;
}

export interface AdjustInventoryData {
  adjustmentType: "add" | "remove" | "wastage";
  quantity: number;
  reason: string;
  batchNo?: string;
  expiryDate?: string;
}

const inventoryAPI = {
  getInventory: async (params?: {
    pageSize?: number;
    pageNo?: number;
    productId?: string;
    branchId?: string;
    salesPointId?: string;
    warehouseId?: string;
    search?: string;
    lowStock?: boolean;
    productType?: string;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<{ data: InventoryItem[]; total: number }> => {
    const response = await api.get("/inventory", { params });
    const { data, total } = parsePaginatedList<InventoryItem>(response.data.data);
    return { data, total };
  },

  getInventoryById: async (id: string): Promise<InventoryItem> => {
    const response = await api.get(`/inventory/${id}`);
    return response.data.data;
  },

  getInventoryByProduct: async (
    productId: string
  ): Promise<InventoryItem[]> => {
    const response = await api.get(`/inventory/product/${productId}`);
    return response.data.data;
  },

  getInventoryByBranch: async (branchId: string): Promise<InventoryItem[]> => {
    const response = await api.get(`/inventory/branch/${branchId}`);
    return response.data.data;
  },

  createInventory: async (
    data: CreateInventoryData
  ): Promise<InventoryItem> => {
    const response = await api.post("/inventory", data);
    return response.data.data;
  },

  updateInventory: async (
    id: string,
    data: UpdateInventoryData
  ): Promise<InventoryItem> => {
    const response = await api.put(`/inventory/${id}`, data);
    return response.data.data;
  },

  adjustInventory: async (
    id: string,
    data: AdjustInventoryData
  ): Promise<InventoryItem> => {
    const response = await api.post(`/inventory/${id}/adjust`, data);
    return response.data.data;
  },

  getLowStockItems: async (): Promise<InventoryItem[]> => {
    const response = await api.get("/inventory/low-stock");
    return response.data.data;
  },

  getInventoryReport: async (): Promise<any> => {
    const response = await api.get("/inventory/report");
    return response.data.data;
  },

  deleteInventory: async (id: string): Promise<void> => {
    await api.delete(`/inventory/${id}`);
  },
};

export default inventoryAPI;
